/**
 * Prescription Templates list + builder (PRD.218).
 *
 * Reusable clinical prescription presets. Org-scoped, may contain
 * inventory-linked or free-text medicine rows. The encounter "apply
 * template" flow belongs to PRD.219 and is intentionally not implemented
 * here.
 */

import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@helios/auth-firebase-web';
import {
  NavIcon,
  PageHeader,
  useBrandTokens,
  type ShellOutletContext,
} from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qPrescriptionTemplates,
  type PrescriptionTemplateListRow,
  type PrescriptionTemplateStatusFilter,
} from '@helios/data-access';
import { PrescriptionTemplateBuilder } from './PrescriptionTemplateBuilder';

// ─── Helpers ───

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ─── Page ───

export function PrescriptionTemplatesPage() {
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? null;
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { collapsed, onToggleSidebar } = useOutletContext<ShellOutletContext>();
  const { db } = usePowerSyncDb();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PrescriptionTemplateStatusFilter>('active');
  const [editing, setEditing] = useState<PrescriptionTemplateListRow | 'new' | null>(null);

  const { data: templates = [] } = usePowerSyncQuery<PrescriptionTemplateListRow>(
    qPrescriptionTemplates({ orgId, search, status }),
  );

  // Stable copy for the active-template count header.
  const allActiveCount = useMemo(
    () => templates.filter((t) => t.is_active === 1).length,
    [templates],
  );

  if (editing) {
    return (
      <PrescriptionTemplateBuilder
        templateId={editing === 'new' ? null : editing.id}
        orgId={orgId}
        userId={userId}
        db={db}
        collapsed={collapsed}
        onToggleSidebar={onToggleSidebar}
        onClose={() => setEditing(null)}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Prescription Templates"
        subtitle="Create and manage reusable prescription presets for common diagnoses and treatments."
        collapsed={collapsed}
        onToggle={onToggleSidebar}
      >
        <button
          onClick={() => setEditing('new')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 16px',
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: primary,
            color: '#fff',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <NavIcon name="plus" />
          New Template
        </button>
      </PageHeader>

      {/* Filter bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          padding: '0 var(--sp-6) var(--sp-4)',
          flexShrink: 0,
        }}
      >
        <div style={{ position: 'relative', flex: 1, maxWidth: 480 }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, category, or medicine…"
            style={{
              width: '100%',
              padding: '8px 12px 8px 36px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              fontSize: 'var(--font-size-sm)',
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          <span
            style={{
              position: 'absolute',
              top: 8,
              left: 10,
              color: 'var(--text-muted)',
              pointerEvents: 'none',
            }}
          >
            <NavIcon name="search" />
          </span>
        </div>

        <StatusFilter value={status} onChange={setStatus} />

        <span
          style={{
            marginLeft: 'auto',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
          }}
        >
          {templates.length} shown · {allActiveCount} active
        </span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 var(--sp-6) var(--sp-6)' }}>
        {templates.length === 0 ? (
          <EmptyState onCreateNew={() => setEditing('new')} primary={primary} />
        ) : (
          <TemplateTable
            templates={templates}
            onOpen={(t) => setEditing(t)}
            onToggleActive={async (t) => {
              if (!db) return;
              const next = t.is_active === 1 ? 0 : 1;
              if (
                next === 0 &&
                !window.confirm(
                  'Deactivate this template?\nIt will remain saved but will no longer appear as a suggested active template.',
                )
              ) {
                return;
              }
              await db.execute(
                `UPDATE prescription_templates SET is_active = ?, updated_at = ? WHERE id = ?`,
                [next, new Date().toISOString(), t.id],
              );
            }}
          />
        )}
      </div>
    </div>
  );
}

// ─── Status filter ───

function StatusFilter({
  value,
  onChange,
}: {
  value: PrescriptionTemplateStatusFilter;
  onChange: (v: PrescriptionTemplateStatusFilter) => void;
}) {
  const options: Array<{ value: PrescriptionTemplateStatusFilter; label: string }> = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'all', label: 'All' },
  ];
  return (
    <div
      style={{
        display: 'inline-flex',
        borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        overflow: 'hidden',
      }}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            style={{
              padding: '6px 12px',
              border: 'none',
              background: active ? 'var(--gradient-nav-active)' : 'transparent',
              color: active ? 'var(--text)' : 'var(--text-muted)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: active ? 600 : 500,
              cursor: 'pointer',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Empty state ───

function EmptyState({ onCreateNew, primary }: { onCreateNew: () => void; primary: string }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        padding: 'var(--sp-10) var(--sp-5)',
        color: 'var(--text-muted)',
      }}
    >
      <NavIcon name="clipboard" />
      <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 500, color: 'var(--text)' }}>
        No prescription templates yet.
      </div>
      <div style={{ fontSize: 'var(--font-size-sm)' }}>
        Create your first template to speed up common prescriptions.
      </div>
      <button
        onClick={onCreateNew}
        style={{
          marginTop: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 16px',
          borderRadius: 'var(--radius-sm)',
          border: 'none',
          background: primary,
          color: '#fff',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        <NavIcon name="plus" />
        New Template
      </button>
    </div>
  );
}

// ─── Table ───

function TemplateTable({
  templates,
  onOpen,
  onToggleActive,
}: {
  templates: PrescriptionTemplateListRow[];
  onOpen: (t: PrescriptionTemplateListRow) => void;
  onToggleActive: (t: PrescriptionTemplateListRow) => void;
}) {
  return (
    <div
      style={{
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        overflow: 'hidden',
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--bg-surface-2, #fafafa)' }}>
            <Th>Name</Th>
            <Th>Category / Specialty</Th>
            <Th style={{ textAlign: 'right' }}>Medicines</Th>
            <Th>Status</Th>
            <Th>Last Updated</Th>
            <Th style={{ textAlign: 'right' }}>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {templates.map((t) => (
            <tr
              key={t.id}
              style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
              onClick={() => onOpen(t)}
            >
              <Td>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--text)',
                  }}
                >
                  {t.name}
                </div>
                {t.description && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                      marginTop: 2,
                      maxWidth: 320,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t.description}
                  </div>
                )}
              </Td>
              <Td>
                <span style={mutedTextStyle}>
                  {[t.category, t.specialty].filter(Boolean).join(' · ') || '—'}
                </span>
              </Td>
              <Td style={{ textAlign: 'right' }}>
                <span style={mutedTextStyle}>{t.medicine_count}</span>
              </Td>
              <Td>
                {t.is_active === 1 ? (
                  <StatusPill kind="active">Active</StatusPill>
                ) : (
                  <StatusPill kind="inactive">Inactive</StatusPill>
                )}
              </Td>
              <Td>
                <span style={mutedTextStyle}>{formatDate(t.updated_at)}</span>
              </Td>
              <Td style={{ textAlign: 'right' }}>
                <div
                  style={{ display: 'inline-flex', gap: 6 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button onClick={() => onOpen(t)} style={rowBtnStyle}>
                    Edit
                  </button>
                  <button onClick={() => onToggleActive(t)} style={rowBtnStyle}>
                    {t.is_active === 1 ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '10px 12px',
        fontSize: 11,
        fontWeight: 600,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <td
      style={{
        padding: '10px 12px',
        verticalAlign: 'middle',
        fontSize: 'var(--font-size-sm)',
        color: 'var(--text)',
        ...style,
      }}
    >
      {children}
    </td>
  );
}

function StatusPill({
  kind,
  children,
}: {
  kind: 'active' | 'inactive';
  children: React.ReactNode;
}) {
  const styles: React.CSSProperties =
    kind === 'active'
      ? {
          background: 'var(--color-success-bg, #dcfce7)',
          color: 'var(--color-success-text, #166534)',
        }
      : {
          background: 'var(--bg-surface-2, #f1f5f9)',
          color: 'var(--text-muted)',
        };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 'var(--radius-full)',
        fontSize: 10,
        fontWeight: 600,
        ...styles,
      }}
    >
      {children}
    </span>
  );
}

const mutedTextStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text-muted)',
};

const rowBtnStyle: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 500,
  cursor: 'pointer',
};
